import React, { useState, useEffect } from 'react';
import Header from './Header';
import './scss/activityLog.scss';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from './services/apiService';

function ActivityLog() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [availableTeams, setAvailableTeams] = useState([]);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const playerId = sessionStorage.getItem('player_id');
        
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_activity_log.php?team_id=${teamId}&player_id=${playerId}`);
        const data = response.data;
        
        if (data.success) {
          setActivities(data.activities);
          
          // Extract unique teams from activities for filtering
          const teams = [...new Set(data.activities.map(activity => activity.team_id).filter(Boolean))];
          const teamOptions = teams.map(teamId => {
            const activity = data.activities.find(a => a.team_id === teamId);
            return {
              id: teamId,
              name: activity?.team_name || `Team ${teamId}`
            };
          });
          setAvailableTeams(teamOptions);
          
          // Set default filter to current team
          setSelectedTeamFilter(teamId);
        } else {
          setError(data.message || 'Failed to fetch activities');
        }
      } catch (error) {
        console.error('Error fetching activities:', error);
        setError('Failed to fetch activities. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'response': return '📝';
      case 'login': return '🔑';
      case 'announcement': return '📢';
      case 'achievement': return '🏆';
      case 'player_details_updated': return '✏️';
      case 'parent_invitation': return '👨‍👩‍👧‍👦';
      default: return '📌';
    }
  };

  const formatMessage = (activity) => {
    switch (activity.type) {
      case 'response':
        return `${activity.team_name || 'Unknown Team'} ${activity.message}`;
      
      case 'parent_invitation':
        return activity.message;
      
      case 'achievement':
        let achievementName;
        switch (activity.message) {
          case 'hat_trick':
            achievementName = 'Hat-trick Hero';
            break;
          case 'star_performer':
            achievementName = 'Star Performer';
            break;
          case 'motm_master':
            achievementName = 'POTM Master';
            break;
          case 'goal_machine':
            achievementName = 'Goal Machine';
            break;
          case 'team_player':
            achievementName = 'Team Player';
            break;
          case 'perfect_attendance':
            achievementName = 'Perfect Attendance';
            break;
          case 'comeback_king':
            achievementName = 'Comeback King';
            break;
          case 'clean_sheet':
            achievementName = 'Clean Sheet';
            break;
          case 'quick_starter':
            achievementName = 'Quick Starter';
            break;
          case 'winning_streak':
            achievementName = 'Winning Streak';
            break;
          default:
            achievementName = activity.message;
        }
        return `${activity.team_name || 'Unknown Team'} has earned the ${achievementName} achievement!`;
      
      default:
        return activity.message;
    }
  };

  const filteredActivities = selectedTeamFilter === 'all' 
    ? activities 
    : activities.filter(activity => activity.team_id == selectedTeamFilter);

  if (loading) return <div className="loading">Loading...</div>;

  if (error) {
    return (
      <div className="ActivityLog container">
        <Header />
        <div className="button-container">
          <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        </div>
        <h1>Activity Log</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="ActivityLog container">
      <Header />
      <div className="button-container">
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
      </div>
      <h1>Activity Log</h1>
      
      {/* Team Filter */}
      {availableTeams.length > 1 && (
        <div className="team-filter">
          <label>Filter by Team:</label>
          <div className="filter-pills">
            <button
              className={`filter-pill ${selectedTeamFilter === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedTeamFilter('all')}
            >
              All Teams
            </button>
            {availableTeams.map(team => (
              <button
                key={team.id}
                className={`filter-pill ${selectedTeamFilter == team.id ? 'active' : ''}`}
                onClick={() => setSelectedTeamFilter(team.id)}
              >
                {team.name}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="activity-list">
        {filteredActivities.map((activity) => (
          <div key={activity.notification_id} className="activity-item">
            <div className="activity-icon">
              {getActivityIcon(activity.type)}
            </div>
            <div className="activity-content">
              <div className="activity-header">
                <div className="activity-left">
                  {!['response', 'achievement'].includes(activity.type) && (
                    <span className="player-name">{activity.team_name || 'Unknown Team'}</span>
                  )}
                  <span className="activity-message">{formatMessage(activity)}</span>
                </div>
                <div className="activity-time-container">
                  <span className="activity-time">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </span>
                  <span className="activity-exact-time">
                    {new Date(activity.created_at).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredActivities.length === 0 && (
          <div className="no-activities">
            {selectedTeamFilter === 'all' 
              ? 'No activities to display' 
              : 'No activities found for the selected team'
            }
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityLog; 